/**
 * LogThisMonthModal · Jul 2026
 * ================================================================
 * Compact tabbed modal launched from the "Log this month" FAB.
 *
 * Two tabs:
 *   1. REVENUE  — one number (this month's revenue), PATCHed to
 *      `/api/businesses/{id}/revenue-actuals/{YYYY-MM}` under a
 *      single "Total revenue" category. Idempotent per month.
 *   2. SNAPSHOT — Operating Health % (0..100), POSTed to
 *      `/api/businesses/{id}/operating-health/snapshot`. Optional
 *      per-leg breakdown fields (revenue/runway/compliance/activity).
 *
 * On success:
 *   • Sonner toast with the amount / pct
 *   • Optional "See it on the trajectory" CTA that navigates to the
 *     Cohort P&L (for revenue) or Weekly Review (for snapshot).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Activity, DollarSign, X } from "lucide-react";
import { api } from "../../lib/apiClient";

const REVENUE_CATEGORY = "Total revenue";

function currentUtcMonth() {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function monthLabel(ym) {
  try {
    const [y, m] = String(ym).split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return ym;
  }
}

export default function LogThisMonthModal({ open, onClose, business, onLogged, initialTab = "revenue" }) {
  const businessId = business?.id;
  const [tab, setTab] = useState(initialTab);
  const [saving, setSaving] = useState(false);
  const [successCta, setSuccessCta] = useState(null);
  const month = useMemo(() => currentUtcMonth(), []);

  // Pre-fill last month's revenue so a founder can accept-with-one-click.
  const lastMonthRevenue = useMemo(() => {
    const rev = business?.budget_revenue_actuals || {};
    const months = Object.keys(rev).sort();
    if (months.length === 0) return "";
    const last = months[months.length - 1];
    const row = rev[last] || {};
    const total = Object.values(row).reduce(
      (s, v) => (Number.isFinite(Number(v)) ? s + Number(v) : s),
      0,
    );
    return total > 0 ? String(total) : "";
  }, [business]);

  const [revenueValue, setRevenueValue] = useState(lastMonthRevenue);
  const [snapshotPct, setSnapshotPct] = useState("");

  useEffect(() => {
    if (!open) return;
    setTab(initialTab === "snapshot" ? "snapshot" : "revenue");
    setSuccessCta(null);
    setRevenueValue(lastMonthRevenue);
    setSnapshotPct("");
    // Deps intentionally minimal — the reset must fire only on
    // open->true transitions or when the caller changes initialTab
    // between opens. lastMonthRevenue is captured fresh in the
    // closure; adding it to deps would re-fire on parent refetch
    // (post-log) and wipe the just-set successCta (iteration_122).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTab]);

  // ⌨ ESC to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!businessId) return;
    setSaving(true);
    try {
      if (tab === "revenue") {
        const amt = Number(revenueValue);
        if (!Number.isFinite(amt) || amt < 0) {
          toast.error("Enter a valid revenue amount");
          setSaving(false);
          return;
        }
        await api.patch(`/businesses/${businessId}/revenue-actuals/${month}`, {
          actuals: { [REVENUE_CATEGORY]: amt },
        });
        toast.success(
          `${monthLabel(month)} revenue logged · $${amt.toLocaleString()}`,
        );
        setSuccessCta({
          label: "See it on the trajectory",
          to: `/business/${businessId}/cohorts`,
          testid: "log-success-cta-cohorts",
        });
      } else {
        const pct = Number(snapshotPct);
        if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
          toast.error("Snapshot must be 0–100");
          setSaving(false);
          return;
        }
        await api.post(`/businesses/${businessId}/operating-health/snapshot`, {
          pct: Math.round(pct),
        });
        toast.success(`Operating Health snapshot logged · ${Math.round(pct)}/100`);
        // Feb 2027 · Weekly Review retired — return the founder to
        // the Hub where the trajectory now lives inline.
        setSuccessCta({
          label: "See it on the trajectory",
          to: `/business/${businessId}?full=1`,
          testid: "log-success-cta-hub",
        });
      }
      onLogged?.();
    } catch (err) {
      // Surface backend error text when available.
      const msg =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail ||
        "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="log-this-month-backdrop"
    >
      <div
        className="mt-[10vh] w-[calc(100%-2rem)] max-w-lg bg-white border border-[#4A4458] shadow-[8px_8px_0px_rgba(74,68,88,1)]"
        style={{ borderRadius: 0 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Log this month"
        data-testid="log-this-month-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.24em] text-[#4A4458]">
              Log this month
            </div>
            <div className="text-lg font-bold text-[#4A4458]" data-testid="log-modal-month">
              {monthLabel(month)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-neutral-500 hover:text-[#4A4458] transition-colors"
            aria-label="Close"
            data-testid="log-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-neutral-200">
          <button
            type="button"
            onClick={() => setTab("revenue")}
            className={`flex-1 px-6 py-3 text-[11px] font-black uppercase tracking-[0.16em] border-b-2 transition-colors inline-flex items-center justify-center gap-2 ${
              tab === "revenue"
                ? "border-[#7C5FB0] text-[#7C5FB0]"
                : "border-transparent text-neutral-500 hover:text-[#4A4458]"
            }`}
            data-testid="log-tab-revenue"
          >
            <DollarSign className="w-4 h-4" aria-hidden />
            Revenue
          </button>
          <button
            type="button"
            onClick={() => setTab("snapshot")}
            className={`flex-1 px-6 py-3 text-[11px] font-black uppercase tracking-[0.16em] border-b-2 transition-colors inline-flex items-center justify-center gap-2 ${
              tab === "snapshot"
                ? "border-[#7C5FB0] text-[#7C5FB0]"
                : "border-transparent text-neutral-500 hover:text-[#4A4458]"
            }`}
            data-testid="log-tab-health"
          >
            <Activity className="w-4 h-4" aria-hidden />
            Snapshot
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {tab === "revenue" ? (
            <>
              <label
                className="block text-[10px] font-black uppercase tracking-[0.24em] text-[#4A4458] mb-2"
                htmlFor="log-revenue-input"
              >
                Total revenue in {monthLabel(month)}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 font-bold">
                  $
                </span>
                <input
                  id="log-revenue-input"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={revenueValue}
                  onChange={(e) => setRevenueValue(e.target.value)}
                  placeholder={lastMonthRevenue || "0"}
                  className="w-full pl-8 pr-4 py-3 border border-[#4A4458] bg-white text-lg font-bold text-[#4A4458] focus:outline-none focus:ring-2 focus:ring-[#7C5FB0]"
                  style={{ borderRadius: 0 }}
                  data-testid="log-revenue-input"
                />
              </div>
              {lastMonthRevenue && (
                <div className="mt-2 text-[11px] text-neutral-500">
                  Pre-filled with last month&apos;s total (${Number(lastMonthRevenue).toLocaleString()}).
                  Edit or accept.
                </div>
              )}
            </>
          ) : (
            <>
              <label
                className="block text-[10px] font-black uppercase tracking-[0.24em] text-[#4A4458] mb-2"
                htmlFor="log-snapshot-input"
              >
                Operating Health today (0–100)
              </label>
              <input
                id="log-snapshot-input"
                type="number"
                inputMode="numeric"
                min="0"
                max="100"
                step="1"
                value={snapshotPct}
                onChange={(e) => setSnapshotPct(e.target.value)}
                placeholder="e.g. 72"
                className="w-full px-4 py-3 border border-[#4A4458] bg-white text-lg font-bold text-[#4A4458] focus:outline-none focus:ring-2 focus:ring-[#7C5FB0]"
                style={{ borderRadius: 0 }}
                data-testid="log-snapshot-input"
              />
              <div className="mt-2 text-[11px] text-neutral-500">
                One integer 0–100. Idempotent per day — a second log today overwrites.
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          {successCta ? (
            <Link
              to={successCta.to}
              onClick={onClose}
              className="text-[11px] font-black uppercase tracking-[0.16em] text-[#7C5FB0] hover:underline"
              data-testid={successCta.testid}
            >
              → {successCta.label}
            </Link>
          ) : (
            <span className="text-[11px] text-neutral-500">
              Founder-triggered. Never automatic.
            </span>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[11px] font-black uppercase tracking-[0.16em] text-neutral-600 hover:text-[#4A4458]"
              data-testid="log-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="px-5 py-2 bg-[#7C5FB0] text-white text-[11px] font-black uppercase tracking-[0.16em] hover:bg-[#6a4f9e] active:translate-y-[1px] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ borderRadius: 0 }}
              data-testid="log-submit-button"
            >
              {saving ? "Saving…" : "Log it"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
